import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { formatMultiple } from './lib/format-multiple.js';
import { formatInZone } from './lib/time.js';
import { Time } from './time.js';
/**
 * Фраза движка с подстановками `{{key}}` (например, «сильные отдыхают до
 * {{restUntil}}»). Движок не знает таймзону админа, поэтому даты он отдаёт
 * моментом, а пишет их админка — общим `Time` в таймзоне админа; кратности —
 * общим `formatMultiple`. Календарный день (`day`, например дни недели
 * отчёта) — без перевода в зону: 28.09 остаётся 28.09 и в Москве, и в LA.
 */
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Календарный день `YYYY-MM-DD` шаблоном date-fns (по-русски), БЕЗ перевода
 * в таймзону: день считается полуночью UTC и пишется в UTC, так что день не
 * сдвигается ни в одной зоне. Битый или несуществующий день — ''.
 */
export function formatCalendarDay(ymd, pattern) {
    if (!ymd || !DAY_RE.test(ymd))
        return '';
    const d = new Date(`${ymd}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== ymd)
        return '';
    return formatInZone(d, pattern, 'UTC');
}
/**
 * Как писать календарный день при пресете дат фразы: дни — без времени, в
 * той же «длине», что и даты рядом («12.10» к «12.10», «28 сент. 2026» к
 * «28 сент. 2026, 03:00»).
 */
const DAY_PATTERN_BY_PRESET = {
    short: 'd MMM',
    dateTime: 'd MMM yyyy',
    date: 'd MMM yyyy',
    time: 'd MMM yyyy',
    long: 'd MMMM yyyy',
    shortNumeric: 'dd.MM',
    dateTimeNumeric: 'dd.MM.yyyy',
    timestamp: 'dd.MM.yyyy',
    dayMonthNumeric: 'dd.MM',
};
/** Известные типы подстановок; иной тип (новый движок) — запасная фраза целиком. */
const KNOWN_VAR_TYPES = new Set(['date', 'day', 'multiple', 'text']);
/** Плейсхолдер: ключ из букв, цифр и `_` (как `CONTENT_TEMPLATE_VAR_KEY`). */
const PLACEHOLDER = /\{\{([A-Za-z0-9_]+)\}\}/g;
/**
 * Разбор шаблона на куски. null — шаблон показывать нельзя, нужна запасная
 * фраза целиком: шаблона нет или он пуст; в нём ключ, которого нет в
 * значениях; значение неизвестного типа (движок новее админки); или
 * остались непарные `{{`/`}}` (кривой плейсхолдер вроде `{{ key }}` иначе
 * попал бы на экран как есть). Полуподставленная фраза хуже запасной.
 */
export function parseContentTemplate(template, vars) {
    if (!template || template.trim() === '')
        return null;
    const segments = [];
    let last = 0;
    for (const m of template.matchAll(PLACEHOLDER)) {
        const key = m[1];
        const value = vars != null && Object.hasOwn(vars, key) ? vars[key] : undefined;
        if (value === undefined)
            return null;
        if (!KNOWN_VAR_TYPES.has(value.type))
            return null;
        if (value.type === 'day' && formatCalendarDay(value.date, 'yyyy') === '')
            return null;
        if (m.index > last)
            segments.push({ kind: 'text', text: template.slice(last, m.index) });
        segments.push({ kind: 'var', key, value });
        last = m.index + m[0].length;
    }
    if (last < template.length)
        segments.push({ kind: 'text', text: template.slice(last) });
    if (segments.some((s) => s.kind === 'text' && (s.text.includes('{{') || s.text.includes('}}')))) {
        return null;
    }
    return segments;
}
/**
 * Фраза движка: шаблон с датами в таймзоне админа, а если его нет или он
 * битый — запасная фраза как есть. Ничего нет — ничего не рисует.
 */
export function ContentTemplateText({ template, vars, fallback, datePreset = 'dayMonthNumeric', }) {
    const segments = parseContentTemplate(template, vars);
    if (segments === null)
        return fallback ? _jsx(_Fragment, { children: fallback }) : null;
    return (_jsx(_Fragment, { children: segments.map((s, i) => {
            if (s.kind === 'text')
                return _jsx(React.Fragment, { children: s.text }, i);
            const v = s.value;
            if (v.type === 'date')
                return _jsx(Time, { iso: v.at, preset: datePreset }, i);
            if (v.type === 'day')
                return (_jsx("time", { dateTime: v.date, children: formatCalendarDay(v.date, DAY_PATTERN_BY_PRESET[datePreset]) }, i));
            if (v.type === 'multiple')
                return _jsx(React.Fragment, { children: formatMultiple(v.value) }, i);
            return _jsx(React.Fragment, { children: v.value }, i);
        }) }));
}
/** Есть ли что показать: шаблон сходится или есть запасная фраза. */
export function hasContentTemplateText(template, vars, fallback) {
    return parseContentTemplate(template, vars) !== null || !!fallback;
}
/** Календарный день UTC момента `iso` (`YYYY-MM-DD`); битый момент — null. */
function utcDayOf(ms) {
    if (Number.isNaN(ms))
        return null;
    return new Date(ms).toISOString().slice(0, 10);
}
/**
 * Период недельного отчёта календарными днями: «28 сент. – 4 окт. 2026».
 * Неделя движка — полуинтервал [пн 00:00 UTC; след. пн 00:00 UTC), поэтому
 * дни берутся по UTC (последний — день момента `end − 1 мс`) и пишутся без
 * времени и без перевода в зону админа: в Москве и в Лос-Анджелесе одна и та
 * же неделя пн–вс. Год у первого дня — только если годы разные. Битые
 * границы — ничего не рисует.
 */
export function ContentReportPeriod({ start, end, className }) {
    if (!start || !end)
        return null;
    const from = utcDayOf(new Date(start).getTime());
    const endMs = new Date(end).getTime();
    const to = utcDayOf(endMs - 1);
    if (from === null || to === null || to < from)
        return null;
    const full = 'd MMM yyyy';
    if (from === to) {
        return (_jsx("span", { className: className, children: _jsx("time", { dateTime: from, children: formatCalendarDay(from, full) }) }));
    }
    const sameYear = from.slice(0, 4) === to.slice(0, 4);
    return (_jsxs("span", { className: className, children: [_jsx("time", { dateTime: from, children: formatCalendarDay(from, sameYear ? 'd MMM' : full) }), ' – ', _jsx("time", { dateTime: to, children: formatCalendarDay(to, full) })] }));
}
//# sourceMappingURL=content-template.js.map